import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";

/**
 * CuentaSelect
 * ─────────────────────────────────────────────────────────────────────────────
 * Selector de cuentas contables de detalle.
 * Reemplaza el selector de "Rubro" en Facturas y el de categoría en CxP.
 *
 * Props:
 *   value       → id de la cuenta seleccionada (número)
 *   onChange    → fn(id, cuenta) → llamado cuando el usuario selecciona
 *   tipo        → "INGRESO" | "GASTO" | null (sin filtro)
 *   placeholder → texto de placeholder
 *   disabled    → boolean
 *   error       → string con mensaje de error
 *   className   → clase CSS adicional
 *
 * Comportamiento:
 *   - Muestra solo cuentas is_detalle=True y activa=True
 *   - Muestra el camino completo: código + nombre
 *   - Permite búsqueda por código o nombre
 *   - Se integra en el estilo del sistema existente
 */
export function CuentaSelect({
  value,
  onChange,
  tipo = null,
  placeholder = "Seleccionar cuenta contable…",
  disabled = false,
  error,
  className = "form-select",
}) {
  const { t } = useTranslation();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = tipo
      ? `/api/finance/catalogo/detalle/?tipo=${tipo}`
      : "/api/finance/catalogo/detalle/";

    apiFetch(url)
      .then((r) => r.json())
      .then((d) => setCuentas(Array.isArray(d) ? d : []))
      .catch(() => setCuentas([]))
      .finally(() => setLoading(false));
  }, [tipo]);

  function handleChange(e) {
    const id     = Number(e.target.value);
    const cuenta = cuentas.find((c) => c.id === id) ?? null;
    onChange(id, cuenta);
  }

  const borderStyle = error
    ? { borderColor: "#ef4444", boxShadow: "0 0 0 2px rgba(239,68,68,0.15)" }
    : undefined;

  return (
    <div>
      <select
        className={className}
        value={value || ""}
        onChange={handleChange}
        disabled={disabled || loading}
        style={borderStyle}
      >
        <option value="">
          {loading ? t("common.cargando") : placeholder}
        </option>

        {cuentas.map((c) => (
          <option key={c.id} value={c.id}>
            {c.codigo}  —  {c.nombre}
          </option>
        ))}
      </select>

      {error && (
        <div style={{
          color: "#f87171", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.7rem" }} />
          {error}
        </div>
      )}

      {!loading && cuentas.length === 0 && (
        <div style={{
          color: "#f59e0b", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "0.7rem" }} />
          {tipo
            ? t("catalogo.sinCuentasTipo", { tipo })
            : t("catalogo.sinCuentas")
          }
        </div>
      )}
    </div>
  );
}

/**
 * CuentaSelectSearch
 * ─────────────────────────────────────────────────────────────────────────────
 * Variante con búsqueda en tiempo real — para formularios donde hay muchas cuentas.
 * Muestra un input de búsqueda y un dropdown filtrado.
 */
export function CuentaSelectSearch({
  value,
  onChange,
  tipo = null,
  placeholder = "Buscar cuenta contable…",
  disabled = false,
  error,
}) {
  const { t } = useTranslation();
  const [cuentas,  setCuentas]  = useState([]);
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [rect,     setRect]     = useState(null);
  const anchorRef   = useRef(null);
  const dropdownRef = useRef(null);

  // Cargar cuentas
  useEffect(() => {
    const url = tipo
      ? `/api/finance/catalogo/detalle/?tipo=${tipo}`
      : "/api/finance/catalogo/detalle/";
    apiFetch(url)
      .then((r) => r.json())
      .then((d) => setCuentas(Array.isArray(d) ? d : []))
      .catch(() => setCuentas([]))
      .finally(() => setLoading(false));
  }, [tipo]);

  // Cerrar al click fuera (el dropdown vive en un portal, así que se revisan ambos refs)
  useEffect(() => {
    function handler(e) {
      const insideAnchor   = anchorRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideAnchor && !insideDropdown) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Posicionar el dropdown en coordenadas de viewport (portal a document.body)
  // para que no lo recorten contenedores ancestros con overflow:hidden.
  useEffect(() => {
    if (!open) return;
    function updateRect() {
      if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open]);

  // Si no cabe debajo del ancla, el dropdown se abre hacia arriba y su alto
  // máximo se ajusta al espacio real disponible en el viewport.
  let dropdownTop = 0, dropdownMaxHeight = 280, dropdownWidth = 0, dropdownLeft = 0;
  if (rect) {
    const margin     = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    if (spaceBelow >= 160 || spaceBelow >= spaceAbove) {
      dropdownMaxHeight = Math.max(120, Math.min(280, spaceBelow));
      dropdownTop = rect.bottom + 4;
    } else {
      dropdownMaxHeight = Math.max(120, Math.min(280, spaceAbove));
      dropdownTop = rect.top - dropdownMaxHeight - 4;
    }
    // El panel nunca es más angosto que ~300px, aunque la columna de la
    // grilla del asiento sea angosta — si no, el nombre de la cuenta queda
    // truncado e ilegible. Se recorta para no salirse de la ventana.
    const minWidth = 300;
    dropdownWidth = Math.max(rect.width, minWidth);
    dropdownLeft  = Math.min(rect.left, window.innerWidth - dropdownWidth - margin);
    dropdownLeft  = Math.max(margin, dropdownLeft);
  }
  const listMaxHeight = Math.max(80, dropdownMaxHeight - 44);

  const seleccionada = cuentas.find((c) => c.id === value);
  const filtradas    = cuentas.filter(
    (c) =>
      !search ||
      c.codigo.toLowerCase().includes(search.toLowerCase()) ||
      c.nombre.toLowerCase().includes(search.toLowerCase())
  );

  function seleccionar(cuenta) {
    onChange(cuenta.id, cuenta);
    setSearch("");
    setOpen(false);
  }

  return (
    <div ref={anchorRef} style={{ position: "relative" }}>
      {/* Input de búsqueda / valor seleccionado */}
      <div
        style={{
          display: "flex", alignItems: "center",
          background: "var(--bg-input)", border: `1px solid ${error ? "#ef4444" : "var(--border-card)"}`,
          borderRadius: 6, padding: "8px 10px", cursor: disabled ? "not-allowed" : "pointer",
          gap: 8,
        }}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
      >
        <i className="fa-solid fa-book-open" style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
        <span style={{
          flex: 1, minWidth: 0, fontSize: "0.85rem",
          color: seleccionada ? "var(--text-primary)" : "var(--text-muted)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }} title={seleccionada ? `${seleccionada.codigo} — ${seleccionada.nombre}` : undefined}>
          {seleccionada
            ? `${seleccionada.codigo} — ${seleccionada.nombre}`
            : loading ? t("common.cargando") : placeholder
          }
        </span>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`}
          style={{ color: "var(--text-muted)", fontSize: "0.65rem" }} />
      </div>

      {/* Dropdown — portal a document.body para que no lo recorte ningún
          ancestro con overflow:hidden; se posiciona en coords de viewport. */}
      {open && rect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed", top: dropdownTop, left: dropdownLeft, width: dropdownWidth,
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 8, zIndex: 10050, overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)", maxHeight: dropdownMaxHeight,
          }}
        >
          {/* Búsqueda */}
          <div style={{
            padding: "8px 10px", borderBottom: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            <i className="fa-solid fa-magnifying-glass"
              style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
            <input
              autoFocus
              type="text"
              placeholder={t("catalogo.buscarPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1, background: "none", border: "none",
                color: "var(--text-primary)", fontSize: "0.83rem", outline: "none",
              }}
            />
          </div>

          {/* Lista */}
          <div style={{ overflowY: "auto", maxHeight: listMaxHeight }}>
            {filtradas.length === 0 ? (
              <div style={{ padding: "12px 14px", color: "var(--text-muted)", fontSize: "0.82rem" }}>
                {search ? t("catalogo.sinResultados") : t("catalogo.sinCuentas")}
              </div>
            ) : (
              filtradas.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => seleccionar(c)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", padding: "9px 14px",
                    background: c.id === value ? "rgba(249,115,22,0.12)" : "transparent",
                    border: "none", textAlign: "left", cursor: "pointer",
                    borderBottom: "1px solid var(--border-card)",
                    transition: "background 0.1s",
                  }}
                  onMouseOver={(e) => {
                    if (c.id !== value) e.currentTarget.style.background = "var(--hover-bg-strong)";
                  }}
                  onMouseOut={(e) => {
                    if (c.id !== value) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{
                    fontFamily: "monospace", fontSize: "0.75rem",
                    color: "#f97316", minWidth: 110,
                  }}>
                    {c.codigo}
                  </span>
                  <span style={{
                    color: "var(--text-primary)", fontSize: "0.83rem", flex: 1, minWidth: 0,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }} title={c.nombre}>
                    {c.nombre}
                  </span>
                  {c.id === value && (
                    <i className="fa-solid fa-check" style={{ color: "#f97316", fontSize: "0.7rem" }} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}

      {error && (
        <div style={{
          color: "#f87171", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.7rem" }} />
          {error}
        </div>
      )}
    </div>
  );
}
